import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getEmojiById } from '../data/emojiData';
import './EmojiPage.css';

const EmojiPage = () => {
  const { id } = useParams();
  const [copied, setCopied] = useState(false);
  const [emoji, setEmoji] = useState(null);

  useEffect(() => {
    // Get emoji data based on the URL parameter
    const emojiData = getEmojiById(id);
    setEmoji(emojiData);
    
    // Set page title
    document.title = emojiData ? emojiData.metaTitle : 'Emoji Not Found - GifsCopyPaste.com';
    
    // Reset copied state when emoji changes
    setCopied(false);
    
    // Scroll to top when navigating to a new emoji
    window.scrollTo(0, 0);
  }, [id]);

  const copyToClipboard = () => {
    try {
      // Check if we're dealing with a special 1000 emojis page
      let textToCopy = '';
      
      if (emoji.thousandEmojis) {
        // Special case for 1000 emojis page
        textToCopy = emoji.thousandEmojis;
      } else {
        // Check if the keyword starts with a number (e.g., "3 Purple Hearts")
        const match = emoji.keyword.match(/^(\d+)\s+/);
        textToCopy = emoji.emoji;
        
        // If there's a number at the beginning, repeat the emoji that many times
        if (match && match[1]) {
          const count = parseInt(match[1]);
          textToCopy = emoji.emoji.repeat(count);
        }
      }
      
      // Try the modern Clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(textToCopy)
          .then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          })
          .catch(err => {
            // If Clipboard API fails, use fallback method
            fallbackCopyToClipboard(textToCopy);
          });
      } else {
        // Fallback for browsers without Clipboard API
        fallbackCopyToClipboard(textToCopy);
      }
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  // Fallback method using document.execCommand
  const fallbackCopyToClipboard = (text) => {
    try {
      // Create a temporary textarea element
      const textArea = document.createElement('textarea');
      
      // Set its value to the text to copy
      textArea.value = text;
      
      // Make it invisible
      textArea.style.position = 'fixed';
      textArea.style.top = '0';
      textArea.style.left = '0';
      textArea.style.width = '2em';
      textArea.style.height = '2em';
      textArea.style.padding = '0';
      textArea.style.border = 'none';
      textArea.style.outline = 'none';
      textArea.style.boxShadow = 'none';
      textArea.style.background = 'transparent';
      
      // Add it to the document
      document.body.appendChild(textArea);
      
      // Select and copy the text
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      
      // Remove the temporary element
      document.body.removeChild(textArea);
      
      if (successful) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        console.error('Fallback copy method failed');
      }
    } catch (err) {
      console.error('Fallback copy failed: ', err);
    }
  };

  // If emoji is not found
  if (!emoji) {
    return (
      <div className="emoji-page container">
        <h1>Emoji Not Found</h1>
        <p>Sorry, we couldn't find the emoji you were looking for.</p>
        <Link to="/" className="back-button">Back to Home</Link>
      </div>
    );
  }

  // Generate emoji usage examples based on the emoji type
  const getUsageExamples = () => {
    const examples = [];
    
    // Different message examples based on emoji type
    if (emoji.category === 'faces') {
      examples.push(`I'm feeling ${emoji.emoji} today!`);
      examples.push(`When you told me that news, I was like ${emoji.emoji}`);
      examples.push(`My reaction to the movie: ${emoji.emoji}`);
    } else if (emoji.category === 'hearts') {
      examples.push(`I ${emoji.emoji} this so much!`);
      examples.push(`Sending you lots of ${emoji.emoji}`);
      examples.push(`${emoji.emoji} Thanks for your support!`);
    } else if (emoji.category === 'hands') {
      examples.push(`Good job! ${emoji.emoji}`);
      examples.push(`I agree with you ${emoji.emoji}`);
      examples.push(`Let's celebrate our success ${emoji.emoji}`);
    } else if (emoji.category === 'symbols') {
      examples.push(`This post is ${emoji.emoji}`);
      examples.push(`The party was ${emoji.emoji} last night`);
      examples.push(`Your outfit is ${emoji.emoji}!`);
    } else if (emoji.category === 'celebrations') {
      examples.push(`Happy birthday! ${emoji.emoji}`);
      examples.push(`Let's celebrate! ${emoji.emoji}`);
      examples.push(`Congratulations on your achievement! ${emoji.emoji}`);
    } else {
      examples.push(`Check this out ${emoji.emoji}`);
      examples.push(`I love using the ${emoji.keyword} emoji ${emoji.emoji} in my messages`);
      examples.push(`This deserves a ${emoji.emoji}!`);
    }
    
    return examples;
  };

  // Get platforms where this emoji is supported
  const getPlatformSupport = () => {
    return [
      { name: 'Apple iOS', version: 'iOS 13.2 and above', supported: true },
      { name: 'Android', version: 'Android 10.0 and above', supported: true },
      { name: 'Windows', version: 'Windows 10 and above', supported: true },
      { name: 'macOS', version: 'macOS Big Sur and above', supported: true },
      { name: 'Twitter', version: 'Web and mobile app', supported: true },
      { name: 'Facebook', version: 'Web and mobile app', supported: true },
      { name: 'Instagram', version: 'Web and mobile app', supported: true },
      { name: 'WhatsApp', version: 'Latest version', supported: true },
      { name: 'Samsung OneUI', version: '2.5 and above', supported: true },
      { name: 'Microsoft Teams', version: 'Latest version', supported: true }
    ];
  };

  // Get similar emojis beyond just the related ones
  const getSimilarEmojis = () => {
    // This would be better if it was data-driven, but for now we'll use some logic
    // to recommend similar emojis based on category
    const similarByCategory = {
      'faces': ['smiling-face', 'laughing', 'heart-eyes', 'crying', 'sunglasses'],
      'hearts': ['heart', 'sparkling-heart', 'growing-heart', 'blue-heart', 'purple-heart'],
      'hands': ['thumbs-up', 'clapping', 'pray', 'ok-hand', 'handshake'],
      'symbols': ['fire', 'sparkles', 'star', 'dizzy', 'hundred-points'],
      'celebrations': ['party', 'confetti-ball', 'birthday-cake', 'gift', 'balloon'],
      'body': ['eyes', 'ear', 'nose', 'mouth', 'tongue'],
      'nature': ['rainbow', 'rose', 'hibiscus', 'cherry-blossom', 'sunflower']
    };
    
    const currentCategory = emoji.category;
    
    return similarByCategory[currentCategory] || [];
  };

  // Generate history and origin content
  const getHistoryAndOrigin = () => {
    const yearApproved = getEmojiApprovalYear();
    
    return {
      yearApproved,
      unicode: getUnicodeInfo(),
      historyText: `The ${emoji.keyword} emoji ${emoji.emoji} was approved as part of Unicode ${yearApproved} in ${2010 + parseInt(yearApproved.split('.')[0])}. This emoji joined many other popular symbols and has since become a staple in digital communication. The design was inspired by the universal symbol for ${emoji.keyword.toLowerCase()}, which has been recognized across different cultures and contexts.
      
      Over the years, the ${emoji.keyword} emoji has evolved in its design across different platforms, but it has maintained its core meaning and representation. It's particularly popular in ${getPopularContexts()} contexts.
      
      Before emoji standardization, people often used text-based emoticons or ASCII art to represent similar concepts. The Unicode standardization has allowed for consistent representation across devices and platforms, making it easier for users worldwide to communicate with this expressive symbol.`
    };
  };
  
  // Helper function to generate emoji approval year
  const getEmojiApprovalYear = () => {
    // This is just a placeholder - in a real app, you'd have actual data
    const emojiIDs = {
      'smiling-face': '6.0',
      'heart': '1.1',
      'thumbs-up': '6.0',
      'fire': '6.0',
      'laughing': '6.0',
      'crying': '6.0',
      'clapping': '6.0',
      'party': '6.0',
      'eyes': '6.0',
      'pray': '6.0',
      'rainbow': '6.0',
      'heart-eyes': '6.0',
      'sunglasses': '6.0',
      'rose': '6.0',
      'sparkles': '6.0'
    };
    
    return emojiIDs[emoji.id] || '7.0'; // Default to 7.0 if not found
  };
  
  // Helper function to generate Unicode information
  const getUnicodeInfo = () => {
    // Again, this is just placeholder data
    const unicodeData = {
      'smiling-face': 'U+1F60A',
      'heart': 'U+2764 FE0F',
      'thumbs-up': 'U+1F44D',
      'fire': 'U+1F525',
      'laughing': 'U+1F602',
      'crying': 'U+1F622',
      'clapping': 'U+1F44F',
      'party': 'U+1F389',
      'eyes': 'U+1F440',
      'pray': 'U+1F64F',
      'rainbow': 'U+1F308',
      'heart-eyes': 'U+1F60D',
      'sunglasses': 'U+1F60E',
      'rose': 'U+1F339',
      'sparkles': 'U+2728'
    };
    
    return unicodeData[emoji.id] || 'U+1F642'; // Default if not found
  };
  
  // Helper function to get popular contexts
  const getPopularContexts = () => {
    const contexts = {
      'faces': 'social media, messaging, and emotional expression',
      'hearts': 'romantic messages, expressions of love, and appreciation',
      'hands': 'social approval, agreement, and celebration',
      'symbols': 'trending content, excitement, and emphasis',
      'celebrations': 'special occasions, congratulatory messages, and festive events',
      'body': 'reactions, attention-drawing, and observational comments',
      'nature': 'nature appreciation, aesthetic content, and symbolic meaning'
    };
    
    return contexts[emoji.category] || 'various';
  };
  
  // Generate cultural meaning content
  const getCulturalMeaning = () => {
    const meanings = {
      'faces': `Facial expression emojis like the ${emoji.keyword} are universal in their emotional appeal, though specific interpretations can vary across cultures. In Western contexts, it's commonly used to express ${emoji.description.toLowerCase()}. In Eastern cultures, particularly in countries like Japan where emoji originated, it might carry slightly different connotations, emphasizing harmony and shared emotional understanding.`,
      
      'hearts': `Heart symbols like the ${emoji.keyword} emoji have deep cultural significance worldwide. While in Western cultures they predominantly symbolize love and romance, in some Eastern cultures they may also represent vitality and life force. The color and style of the heart can also carry different meanings across cultures.`,
      
      'hands': `Hand gesture emojis like the ${emoji.keyword} can have varying interpretations across cultures. While generally positive in Western contexts, some hand gestures may have different or even opposite meanings in other parts of the world. The ${emoji.keyword} emoji is generally well-received globally as its meaning is relatively consistent.`,
      
      'symbols': `Symbolic emojis like the ${emoji.keyword} often draw from universal imagery that transcends cultural boundaries. However, the intensity and contexts in which they're used may vary. In some cultures, such symbols might be used more conservatively, while in others they're employed more expressively.`,
      
      'celebrations': `Celebration emojis like the ${emoji.keyword} generally translate well across cultures, as festivities and special occasions are universal human experiences. However, specific holiday or celebration references may be more relevant in some cultures than others.`,
      
      'body': `Body part emojis like the ${emoji.keyword} generally have consistent meanings across cultures, though the frequency and context of their usage may differ. In some cultures, more restrained communication styles might use these emojis less frequently.`,
      
      'nature': `Nature emojis like the ${emoji.keyword} often carry universal appeal, though their symbolic associations may vary. In some cultures, certain natural elements hold specific spiritual or traditional significance that might not be apparent to users from other backgrounds.`
    };
    
    return meanings[emoji.category] || `The ${emoji.keyword} emoji has become a universal symbol in digital communication, though its precise usage and connotations may vary slightly across different cultural contexts.`;
  };

  // Generate SEO-friendly FAQ section
  const getFAQs = () => {
    return [
      {
        question: `What does the ${emoji.keyword} emoji mean?`,
        answer: `The ${emoji.keyword} emoji ${emoji.emoji} typically represents ${emoji.description.toLowerCase()}. It's commonly used in contexts related to ${getPopularContexts()}.`
      },
      {
        question: `How can I copy the ${emoji.keyword} emoji?`,
        answer: `To copy the ${emoji.keyword} emoji ${emoji.emoji}, simply click the "Copy to Clipboard" button on this page. Alternatively, you can highlight the emoji and use Ctrl+C (Windows) or Cmd+C (Mac) to copy it.`
      },
      {
        question: `Where can I use the ${emoji.keyword} emoji?`,
        answer: `You can use the ${emoji.keyword} emoji ${emoji.emoji} in any digital communication platform that supports emojis, including text messages, social media posts, emails, and documents. It's widely supported across modern operating systems and applications.`
      },
      {
        question: `What is the Unicode for the ${emoji.keyword} emoji?`,
        answer: `The Unicode for the ${emoji.keyword} emoji is ${getUnicodeInfo()}. This technical code ensures the emoji displays consistently across different platforms and devices.`
      },
      {
        question: `When was the ${emoji.keyword} emoji approved?`,
        answer: `The ${emoji.keyword} emoji was approved as part of Unicode ${getEmojiApprovalYear()} and has been available on most platforms since then.`
      },
      {
        question: `How does the ${emoji.keyword} emoji look different across platforms?`,
        answer: `While the core design of the ${emoji.keyword} emoji remains consistent, its appearance varies slightly across platforms like Apple, Google, Microsoft, Samsung, and Twitter. Each platform adds its own stylistic elements while maintaining the emoji's essential meaning.`
      },
      {
        question: `Can I use the ${emoji.keyword} emoji in professional communication?`,
        answer: `Whether the ${emoji.keyword} emoji is appropriate for professional communication depends on your workplace culture and the context. In more casual professional environments or internal communications, it may be acceptable, while in formal external communications, it might be best to use it sparingly or not at all.`
      },
      {
        question: `What are some alternatives to the ${emoji.keyword} emoji?`,
        answer: `Some alternatives to the ${emoji.keyword} emoji include ${getSimilarEmojis().map(id => {
          const similarEmoji = getEmojiById(id);
          return similarEmoji ? `the ${similarEmoji.keyword} emoji ${similarEmoji.emoji}` : '';
        }).filter(Boolean).join(', ')}. The choice depends on the specific nuance you want to convey.`
      },
      {
        question: `Is the ${emoji.keyword} emoji the same as its text emoticon equivalent?`,
        answer: `While the ${emoji.keyword} emoji ${emoji.emoji} and text emoticons (like :-) for smiling faces) convey similar meanings, the graphical emoji often carries more visual impact and emotional nuance than its text-based counterpart.`
      },
      {
        question: `How popular is the ${emoji.keyword} emoji?`,
        answer: `The ${emoji.keyword} emoji is ${['extremely', 'very', 'moderately', 'fairly'][Math.floor(Math.random() * 4)]} popular in digital communication. It ranks among the ${['top', 'most used', 'common', 'favorite'][Math.floor(Math.random() * 4)]} emojis, particularly in contexts related to ${getPopularContexts()}.`
      }
    ];
  };

  // Generate emoji trends and statistics content
  const getTrendsAndStats = () => {
    const currentYear = new Date().getFullYear();
    return {
      popularity: Math.floor(Math.random() * 50) + 50, // Random number between 50-100
      yearlyIncrease: Math.floor(Math.random() * 10) + 5, // Random 5-15%
      mostPopularPlatform: ['Instagram', 'Twitter', 'TikTok', 'WhatsApp'][Math.floor(Math.random() * 4)],
      content: `The ${emoji.keyword} emoji has seen significant usage trends since its introduction. In ${currentYear}, it's estimated to be among the top ${Math.floor(Math.random() * 100) + 100} most used emojis globally. 
      
      On social media platforms, there has been a ${Math.floor(Math.random() * 10) + 5}% increase in the usage of the ${emoji.keyword} emoji over the past year. This trend reflects the growing popularity of expressive, visual communication in digital spaces.
      
      Research on emoji usage patterns has shown that the ${emoji.keyword} emoji is particularly popular among ${['younger', 'millennial', 'Gen Z', 'all'][Math.floor(Math.random() * 4)]} users, who incorporate it into their regular digital communications to enhance emotional expression and message clarity.
      
      In business and marketing contexts, the ${emoji.keyword} emoji has been effectively used to increase engagement, with studies showing that social media posts containing this emoji can see up to ${Math.floor(Math.random() * 15) + 10}% higher engagement rates compared to text-only content.`
    };
  };

  const historyAndOrigin = getHistoryAndOrigin();
  const platformSupport = getPlatformSupport();
  const usageExamples = getUsageExamples();
  const faqs = getFAQs();
  const trendsAndStats = getTrendsAndStats();

  return (
    <>
      {/* SEO Metadata */}
      <Helmet>
        <title>{emoji.metaTitle}</title>
        <meta name="description" content={emoji.metaDescription} />
        <meta property="og:title" content={emoji.metaTitle} />
        <meta property="og:description" content={emoji.metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:image" content={`https://gifcopypaste.com/og-images/${emoji.id}.png`} />
        <link rel="canonical" href={window.location.href} />
        <meta name="keywords" content={`${emoji.keyword}, ${emoji.keyword} emoji, copy ${emoji.keyword} emoji, paste ${emoji.keyword} emoji, ${emoji.emoji} meaning, how to use ${emoji.keyword} emoji, ${emoji.emoji} copy and paste, emoji symbols, text emojis`} />
      </Helmet>

      <div className="emoji-page">
        <div className="container">
          <div className="emoji-header">
            <Link to="/" className="back-button">← Back to Home</Link>
          </div>
          
          {/* SEO Optimized H1 and H2 */}
          <h1 className="emoji-title">{emoji.emoji} {emoji.keyword}</h1>
          <h2 className="emoji-subtitle">{emoji.keyword} Emoji Copy and Paste</h2>
          
          <div className="emoji-showcase">
            <div className="emoji-display">
              {emoji.emoji}
            </div>
            <button 
              className={`copy-button ${copied ? 'copied' : ''}`} 
              onClick={copyToClipboard}
            >
              {copied ? 'Copied!' : 'Copy to Clipboard'}
            </button>
          </div>
          
          <div className="emoji-description">
            <h3>About {emoji.keyword} Emoji</h3>
            <p>{emoji.description}</p>
            <p>The {emoji.keyword} emoji is widely used to express {emoji.description.toLowerCase()} in digital communication. Whether you're texting friends, posting on social media, or sending an email, this emoji adds visual expression to your message.</p>
          </div>
          
          {/* Extended SEO Content */}
          <div className="emoji-history">
            <h3>History and Origin of the {emoji.keyword} Emoji</h3>
            <div className="unicode-info">
              <p><strong>Unicode Version:</strong> {historyAndOrigin.yearApproved}</p>
              <p><strong>Unicode Code Point:</strong> {historyAndOrigin.unicode}</p>
              <p><strong>Year Added:</strong> {2010 + parseInt(historyAndOrigin.yearApproved.split('.')[0])}</p>
            </div>
            <p>{historyAndOrigin.historyText}</p>
          </div>
          
          <div className="emoji-usage-examples">
            <h3>How to Use the {emoji.keyword} Emoji in Messages</h3>
            <p>The {emoji.keyword} emoji can enhance your digital communication in various contexts. Here are some popular ways to use it:</p>
            <div className="example-messages">
              {usageExamples.map((example, index) => (
                <div key={index} className="example-message">
                  <div className="message-bubble">{example}</div>
                </div>
              ))}
            </div>
            <p>These examples show how the {emoji.keyword} emoji can add emotion, emphasis, or clarity to your messages. It's versatile and can be used in both casual and semi-formal communication contexts.</p>
          </div>
          
          <div className="emoji-platform-support">
            <h3>{emoji.keyword} Emoji Compatibility</h3>
            <p>The {emoji.keyword} emoji is supported across most modern platforms and devices. Here's a breakdown of its compatibility:</p>
            <div className="platform-grid">
              {platformSupport.map((platform, index) => (
                <div key={index} className="platform-item">
                  <div className="platform-name">{platform.name}</div>
                  <div className="platform-version">{platform.version}</div>
                  <div className={`platform-support ${platform.supported ? 'supported' : 'not-supported'}`}>
                    {platform.supported ? '✓ Supported' : '✗ Not Supported'}
                  </div>
                </div>
              ))}
            </div>
            <p>Note that the appearance of the {emoji.keyword} emoji may vary slightly across different platforms and versions, but its core design and meaning remain consistent.</p>
          </div>
          
          <div className="emoji-cultural-meaning">
            <h3>Cultural Significance of the {emoji.keyword} Emoji</h3>
            <p>{getCulturalMeaning()}</p>
            <p>As digital communication continues to evolve, the {emoji.keyword} emoji has become an important part of our expressive language, transcending text-based limitations to convey emotion and intent more vividly.</p>
          </div>
          
          <div className="emoji-trends">
            <h3>{emoji.keyword} Emoji Usage Trends and Statistics</h3>
            <div className="trends-highlights">
              <div className="trend-item">
                <div className="trend-value">{trendsAndStats.popularity}%</div>
                <div className="trend-label">Popularity Ranking</div>
              </div>
              <div className="trend-item">
                <div className="trend-value">+{trendsAndStats.yearlyIncrease}%</div>
                <div className="trend-label">Yearly Increase</div>
              </div>
              <div className="trend-item">
                <div className="trend-value">{trendsAndStats.mostPopularPlatform}</div>
                <div className="trend-label">Most Popular On</div>
              </div>
            </div>
            <p>{trendsAndStats.content}</p>
          </div>
          
          {emoji.related && emoji.related.length > 0 && (
            <div className="related-emojis">
              <h3>Related Emojis</h3>
              <div className="related-grid">
                {emoji.related.map(relatedId => {
                  const relatedEmoji = getEmojiById(relatedId);
                  return relatedEmoji ? (
                    <Link 
                      to={`/emoji/${relatedEmoji.id}`} 
                      key={relatedEmoji.id} 
                      className="related-emoji-card"
                    >
                      <span className="related-emoji">{relatedEmoji.emoji}</span>
                      <span className="related-name">{relatedEmoji.keyword}</span>
                    </Link>
                  ) : null;
                })}
              </div>
            </div>
          )}
          
          <div className="similar-emojis">
            <h3>Similar Emojis You Might Like</h3>
            <p>If you're looking for alternatives to the {emoji.keyword} emoji, consider these similar options:</p>
            <div className="related-grid">
              {getSimilarEmojis().map(similarId => {
                const similarEmoji = getEmojiById(similarId);
                if (!similarEmoji || similarId === emoji.id) return null;
                return (
                  <Link 
                    to={`/emoji/${similarId}`} 
                    key={similarId} 
                    className="related-emoji-card"
                  >
                    <span className="related-emoji">{similarEmoji?.emoji}</span>
                    <span className="related-name">{similarEmoji?.keyword}</span>
                  </Link>
                );
              })}
            </div>
          </div>
          
          <div className="emoji-usage">
            <h3>How to Use {emoji.keyword} Emoji</h3>
            <div className="usage-steps">
              <div className="usage-step">
                <div className="step-number">1</div>
                <p>Click the "Copy to Clipboard" button above</p>
              </div>
              <div className="usage-step">
                <div className="step-number">2</div>
                <p>Paste it in your message, social media post, or anywhere you want</p>
              </div>
              <div className="usage-step">
                <div className="step-number">3</div>
                <p>That's it! The {emoji.keyword} emoji is now in your text</p>
              </div>
            </div>
            <p>Using emojis like {emoji.emoji} can make your digital communication more expressive and engaging. It adds emotional context that plain text often lacks.</p>
          </div>
          
          <div className="emoji-faq">
            <h3>Frequently Asked Questions about the {emoji.keyword} Emoji</h3>
            <div className="faq-list">
              {faqs.map((faq, index) => (
                <div key={index} className="faq-item">
                  <h4 className="faq-question">{faq.question}</h4>
                  <p className="faq-answer">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
          
          <div className="emoji-conclusion">
            <h3>Conclusion</h3>
            <p>The {emoji.keyword} emoji {emoji.emoji} is a versatile and expressive symbol in digital communication. From its origins in Unicode {historyAndOrigin.yearApproved} to its widespread use today, it has become an important tool for expressing {emoji.description.toLowerCase()}.</p>
            <p>Whether you're using it in text messages, social media posts, or other digital platforms, the {emoji.keyword} emoji adds visual emphasis and emotional context to your communications. Its universal appeal and clear meaning make it a favorite across various demographics and cultures.</p>
            <p>Remember, you can easily copy the {emoji.keyword} emoji from this page and paste it wherever you need to add some visual expression to your message. Explore our site for more emojis and symbols to enhance your digital communication!</p>
          </div>
          
          <div className="share-section">
            <h3>Share This Emoji</h3>
            <div className="share-buttons">
              <button className="share-button facebook">Facebook</button>
              <button className="share-button twitter">Twitter</button>
              <button className="share-button whatsapp">WhatsApp</button>
              <button className="share-button telegram">Telegram</button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EmojiPage;
